import React, { useState, useRef, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useOnClickOutside } from 'hooks';
import { fetchVideos } from 'actions/currency';
import { useDispatch, useSelector } from 'react-redux';
import throttle from 'lodash.throttle';

import {
  DEFAULT_MOBILE_DATE_FORMAT,
  getLocaleUrlPath,
  formatParsedDate,
} from 'utils';

import Icon from 'components/commons/Icon';
import {
  Wrapper,
  Slide,
  Title,
  Text,
  Date,
  Video,
  PlayBtn,
  CloseBtn,
  Modal,
  VideoWrapper,
  SlideButtonRight,
  SlideButtonLeft,
  ShadowRight,
  ShadowLeft,
  SliderWrapper,
  VideoMeta,
  SliderContainer,
  AuthorLink,
  AuthorImg,
  AuthorDesc,
  AuthorTitle,
  AuthorName,
  AuthorWrapper,
} from './styles';

const DELETED_VIDEO = 'Deleted video';

const NewsSlider = () => {
  const dispatch = useDispatch();
  const { formatMessage, locale } = useIntl();
  const [isOpen, setIsOpen] = useState({
    show: false,
    body: 'unset',
  });
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isScrollAllowed, setIsScrollAllowed] = useState({
    right: true,
    left: false,
  });
  const [currentVideo, setCurrentVideo] = useState('');
  const ref = useRef();
  const slider = useRef();
  useOnClickOutside(ref, () => setIsOpen({ show: false, body: 'unset' }));
  const videos = useSelector(state => state.currency?.videos || {});

  useEffect(() => {
    document.body.style.overflow = isOpen.body;
  }, [isOpen]);

  useEffect(() => {
    if (!slider.current) {
      return;
    }

    const throttled = throttle(() => {
      const isLeft = slider.current.scrollLeft >= 20;
      const isRight =
        slider.current.scrollLeft <
        slider.current.scrollWidth - slider.current.clientWidth - 20;

      if (!isRight && videos?.total > videos?.items?.length) {
        dispatch(
          fetchVideos({
            isLoadMore: true,
            page: Math.round(videos.items.length / 5) + 1,
          })
        );
        setIsScrollAllowed({
          left: isLeft,
          right: true,
        });
      } else {
        setIsScrollAllowed({
          left: isLeft,
          right: isRight,
        });
      }
    }, 300);

    slider.current.addEventListener('scroll', throttled);
    // eslint-disable-next-line consistent-return
    return () => slider?.current?.removeEventListener('scroll', throttled);
  }, [slider.current, videos]);

  const handlePlay = url => {
    const urlObject = new URL(url);
    const id = urlObject.searchParams.get('v');

    setIsOpen({ show: true, body: 'hidden' });

    setCurrentVideo(id);
  };

  const handleSlideRight = () => {
    if (!slider.current) return;

    slider.current.scrollLeft = scrollLeft + 198;
    setScrollLeft(slider.current.scrollLeft);
  };

  const handleSlideLeft = () => {
    if (!slider.current) return;

    slider.current.scrollLeft = scrollLeft - 198;
    setScrollLeft(slider.current.scrollLeft);
  };

  return (
    <Wrapper>
      <Title>
        <FormattedMessage id="slider.news.title" />
      </Title>
      {isScrollAllowed.right && (
        <SlideButtonRight onClick={handleSlideRight}>
          <Icon
            name="chartDropArrow"
            style={{ transform: 'rotate(-90deg)', verticalAlign: 'unset' }}
          />
        </SlideButtonRight>
      )}
      {isScrollAllowed.left && (
        <SlideButtonLeft onClick={handleSlideLeft}>
          <Icon
            name="chartDropArrow"
            style={{ transform: 'rotate(90deg)', verticalAlign: 'unset' }}
          />
        </SlideButtonLeft>
      )}

      <SliderContainer>
        <ShadowRight />
        {isScrollAllowed.left && <ShadowLeft />}

        <SliderWrapper aria-hidden="true" ref={slider}>
          {videos?.items?.map(
            ({ thumbnailUrl, title, video, position, published }) =>
              title !== DELETED_VIDEO && (
                <Slide key={position}>
                  <Video img={thumbnailUrl}>
                    <PlayBtn onClick={() => handlePlay(video)}>
                      <Icon name="play" />
                    </PlayBtn>
                  </Video>
                  <VideoMeta>
                    <Text>{title}</Text>
                    <Date>
                      <Icon name="watch" style={{ marginRight: '5px' }} />
                      {formatParsedDate(published, DEFAULT_MOBILE_DATE_FORMAT)}
                    </Date>
                  </VideoMeta>
                </Slide>
              )
          )}
        </SliderWrapper>
      </SliderContainer>

      {isOpen.show && (
        <Modal>
          <VideoWrapper ref={ref}>
            <CloseBtn
              type="button"
              onClick={() => setIsOpen({ show: false, body: 'unset' })}
            >
              x
            </CloseBtn>
            <iframe
              src={`https://www.youtube.com/embed/${currentVideo}`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Embedded youtube"
            />
          </VideoWrapper>
        </Modal>
      )}

      <AuthorLink
        href={`${process.env.APP_MINFIN_API_DOMAIN}${getLocaleUrlPath(
          locale
        )}/authors/kozyrev/`}
      >
        <AuthorWrapper>
          <AuthorImg
            src={`${process.env.APP_MINFIN_API_DOMAIN}/img/userpics/643307.jpg`}
            alt={formatMessage({ id: 'slider.author.name' })}
          />
          <AuthorName>
            <AuthorTitle>
              {formatMessage({ id: 'slider.author.name' })}
            </AuthorTitle>
            <AuthorDesc>{formatMessage({ id: 'slider.analyst' })}</AuthorDesc>
          </AuthorName>
        </AuthorWrapper>

        <Icon
          name="chartDropArrow"
          style={{ transform: 'rotate(-90deg)', verticalAlign: 'unset' }}
        />
      </AuthorLink>
    </Wrapper>
  );
};

export default NewsSlider;
